import type { Zone } from '../../services/zonesApi';
import { zonesApi } from '../../services/zonesApi';
import { ensureUiScope } from '../../services/api';
import { findZoneForEvidenceOverlay } from './overlayEvidenceSnapshot';
import { liveViewCanonicalCameraId } from './liveViewGridCameras';

export function resolveLiveViewTenantScope(): { orgId: string; siteId: string } {
  const scope = ensureUiScope();
  try {
    const sp = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const orgId = (sp.get('org') || sp.get('org_id') || scope.orgId || '').trim();
    const siteId = (sp.get('site') || sp.get('site_id') || scope.siteId || '').trim();
    return { orgId, siteId };
  } catch {
    return { orgId: scope.orgId, siteId: scope.siteId };
  }
}

function text(value: unknown): string {
  return String(value || '').trim();
}

export function zonesForLiveViewCamera(zones: Zone[], cameraId: string): Zone[] {
  const wanted = liveViewCanonicalCameraId(cameraId);
  const list = Array.isArray(zones) ? zones : [];
  if (!wanted) return list;
  const matched = list.filter((zone) => {
    const zoneCamera = liveViewCanonicalCameraId(text(zone.camera_id));
    return !zoneCamera || zoneCamera === wanted;
  });
  return matched;
}

async function listOverlayZones(args: {
  orgId?: string;
  siteId?: string;
  deviceId?: string;
  cameraId?: string;
}): Promise<Zone[]> {
  try {
    const res = await zonesApi.listZones(
      undefined,
      undefined,
      args.deviceId,
      args.cameraId,
      true,
      'warehouse',
      args.orgId || undefined,
      args.siteId || undefined,
    );
    return Array.isArray(res.zones) ? res.zones : [];
  } catch {
    return [];
  }
}

export async function resolveOverlayZonesForCamera(
  cameraId: string,
  edgeDeviceId: string | undefined,
  zoneId?: string,
): Promise<Zone[]> {
  const { orgId, siteId } = resolveLiveViewTenantScope();
  const canonicalCameraId = liveViewCanonicalCameraId(cameraId);
  const deviceId = text(edgeDeviceId) || undefined;

  let zones = await listOverlayZones({
    orgId,
    siteId,
    deviceId,
    cameraId: canonicalCameraId || undefined,
  });
  zones = zonesForLiveViewCamera(zones, canonicalCameraId);

  if (zones.length === 0 && canonicalCameraId) {
    const cameraZones = await listOverlayZones({ orgId, siteId, cameraId: canonicalCameraId });
    zones = zonesForLiveViewCamera(cameraZones, canonicalCameraId);
  }

  if (zones.length === 0 && canonicalCameraId && deviceId) {
    const deviceZones = await listOverlayZones({ orgId, siteId, deviceId });
    zones = zonesForLiveViewCamera(deviceZones, canonicalCameraId);
    if (zones.length === 0) {
      const distinctCameras = new Set(
        deviceZones.map((zone) => liveViewCanonicalCameraId(text(zone.camera_id))).filter(Boolean),
      );
      if (distinctCameras.size <= 1) {
        zones = deviceZones;
      }
    }
  }

  const normalizedZoneId = text(zoneId);
  if (normalizedZoneId && !findZoneForEvidenceOverlay(zones, normalizedZoneId, canonicalCameraId)) {
    try {
      const zone = await zonesApi.getZone(normalizedZoneId);
      if (zone) zones = [zone, ...zones];
    } catch {
      // ignore
    }
  }
  return zones;
}
